import { configureStore } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';
import { AnyAction, PreloadedState, combineReducers } from 'redux';
import { Reducer } from 'react';

import storage from 'redux-persist/lib/storage';
import { persistReducer } from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/es/stateReconciler/autoMergeLevel2';
import rootSaga from 'root-saga';
import { RootState } from 'interfaces';

import customMiddleware from 'middlewares/custom-middleware';
import { authSlice } from 'base/auth/slice';
import { CoreSlice } from 'base/core/slice';
import { updateProfiletSlice } from 'pages/profile/components/profile-update/slice';
import { createSessionSlice } from 'pages/sessions/components/sessions-create/slice';
import { sessionSummarySlice } from 'pages/sessions/components/session-summary/slice';
import { scheduleListSlice } from 'pages/schedules/slice';
import { mediaLibrarySlice } from 'pages/media-library/slice';
import { groupUpdateSlice } from 'pages/groups/edit-group/slice';
import { viewContentSlice } from 'base/common/add-content-modal/slice';
import { articleSlice } from 'pages/articles/slice';
import { mediaModalSlice } from 'base/common/media-modal/slice';
import { chatSlice } from 'pages/sessions/components/message-hub/slice';
import { directChatSlice } from 'pages/sessions/components/direct-chat-hub/slice';
import { viewFormsSlice } from 'pages/forms/slice';
import { usersSlice } from 'pages/users/slice';
import { newsfeedSlice } from 'pages/newsfeed/components/newsfeed-list/slice';
import { dashboardSlice } from 'pages/dashboard/slice';
import { groupsSlice } from '../pages/groups/groups-list/slice';
import { groupSummarySlice } from '../pages/groups/group-summary/slice';
import { reviewSlice } from '../pages/review/components/review-list/components/slice';

export const sagaMiddleware = createSagaMiddleware();

const reducers = combineReducers({
  'feature/base/core': CoreSlice.reducer,
  'feature/base/auth': authSlice.reducer,
  'feature/base/notification': CoreSlice.reducer,
  'feature/profile/edit-profile': updateProfiletSlice.reducer,
  'feature/session/create-session': createSessionSlice.reducer,
  'feature/common/content-upload': viewContentSlice.reducer,
  'feature/schedules': scheduleListSlice.reducer,
  'feature/session/summary': sessionSummarySlice.reducer,
  'feature/groups/list': groupsSlice.reducer,
  'feature/groups/summary': groupSummarySlice.reducer,
  'feature/groups/update': groupUpdateSlice.reducer,
  'feature/media-library': mediaLibrarySlice.reducer,
  'feature/articles': articleSlice.reducer,
  'feature/common/media-modal': mediaModalSlice.reducer,
  'feature/session/chat': chatSlice.reducer,
  'feature/direct-chat': directChatSlice.reducer,
  'feature/forms': viewFormsSlice.reducer,
  'feature/users': usersSlice.reducer,
  'feature/review': reviewSlice.reducer,
  'feature/newsfeed': newsfeedSlice.reducer,
  'feature/dashboard': dashboardSlice.reducer,
});

const persistConfig = {
  key: 'root',
  storage,
  stateReconciler: autoMergeLevel2,
  whitelist: [
    'feature/base/core',
    'feature/base/auth',
  ],
};
const persistedReducer = persistReducer(
  persistConfig,
  reducers as Reducer<any, AnyAction>,
);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) => [
    ...getDefaultMiddleware({
      serializableCheck:
        false,
    }),
    sagaMiddleware,
    customMiddleware,
  ],
});

sagaMiddleware.run(rootSaga);
export default store;

/* this function is used to mock store in tests */
export const setupStore = (preloadedState?: PreloadedState<RootState>) => configureStore({
  reducer: persistedReducer,
  middleware: [],
  preloadedState,
});

export type AppStore = typeof store;
